import { useCallback, useEffect, useRef, useState } from 'react';

export const AUTOSAVE_DELAY_MS = 800;

export interface AutosaveHandle {
  readonly dirty: boolean;
  readonly saving: boolean;
  /** Mark the content dirty and (re)schedule the debounced save. */
  touch(): void;
  /** Save now if anything is pending — mode switches, publish, navigation. */
  flush(): Promise<void>;
  /** Cancel any pending timer (unmount). Pending changes are NOT saved. */
  dispose(): void;
}

/**
 * Framework-free autosave core (specs/changes/consolidated-full-editor R3) —
 * the debounce/flush contract lives here so it is unit-testable; the hook
 * below is a thin React binding. A failed save keeps the dirty flag so the
 * next touch or flush retries.
 */
export function createAutosave(
  save: () => Promise<void> | void,
  onChange: (dirty: boolean, saving: boolean) => void,
  delayMs = AUTOSAVE_DELAY_MS,
): AutosaveHandle {
  let dirty = false;
  let saving = false;
  let timer: number | null = null;

  const emit = () => onChange(dirty, saving);
  const clear = () => {
    if (timer !== null) {
      window.clearTimeout(timer);
      timer = null;
    }
  };
  const run = async () => {
    saving = true;
    emit();
    try {
      await save();
      dirty = false;
    } catch (e) {
      // Dirty stays set — callers surface their own error UI from inside the
      // save function.
      console.error('Autosave failed:', e);
    } finally {
      saving = false;
      emit();
    }
  };

  return {
    get dirty() { return dirty; },
    get saving() { return saving; },
    touch() {
      dirty = true;
      emit();
      clear();
      timer = window.setTimeout(() => {
        timer = null;
        void run();
      }, delayMs);
    },
    async flush() {
      clear();
      if (dirty) await run();
    },
    dispose() {
      clear();
    },
  };
}

/**
 * Debounced autosave with an explicit flush — the pattern the spec editor and
 * list modal each hand-rolled, lifted into one hook. The scheduler reads the
 * save function through a ref so it always saves the latest content, not the
 * closure it was scheduled with.
 */
export function useAutosave(save: () => Promise<void> | void, delayMs = AUTOSAVE_DELAY_MS) {
  const saveRef = useRef(save);
  useEffect(() => { saveRef.current = save; }, [save]);

  const [state, setState] = useState({ dirty: false, saving: false });
  const handleRef = useRef<AutosaveHandle | null>(null);
  if (handleRef.current === null) {
    handleRef.current = createAutosave(
      () => saveRef.current(),
      (dirty, saving) => setState({ dirty, saving }),
      delayMs,
    );
  }
  const handle = handleRef.current;

  useEffect(() => () => handle.dispose(), [handle]);

  const touch = useCallback(() => handle.touch(), [handle]);
  const flush = useCallback(() => handle.flush(), [handle]);

  return { dirty: state.dirty, saving: state.saving, touch, flush };
}
